// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Form, Icon, RichTextInput, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useNavigationDOM, useResponsive, useState} from '@supermove/hooks';
import {
  AccountCalendarModel,
  CalendarIntegrationJobCategoryOptions,
  CalendarIntegrationModel,
  OrganizationModel,
} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import Breadcrumbs from '@shared/design/components/Breadcrumbs';
import Button from '@shared/design/components/Button';
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import FieldInput from '@shared/design/components/Field/FieldInput';
import MultiDropdownCheckboxInput from '@shared/design/components/Field/MultiDropdownCheckboxInput';
import IconButton from '@shared/design/components/IconButton';
import Line from '@shared/design/components/Line';
import Text from '@shared/design/components/Text';
import {AccountCalendarFormFields} from '@shared/modules/AccountCalendar/forms/AccountCalendarForm';
import ProjectStatus, {ProjectStatusType} from '@shared/modules/Project/enums/ProjectStatus';
import UserRole from '@shared/modules/User/enums/UserRole';
import {UserFlowFormToFormType} from '@shared/modules/UserFlows/forms/UserFlowForm';
import HardcodedSystemVariables from '@shared/modules/Variable/enums/HardcodedSystemVariables';
import CompanyAccountCalendarSelect from 'modules/Organization/Settings/Communication/components/CalendarIntegrationPage/CompanyAccountCalendarSelect';
import CompanyAccountSelect from 'modules/Organization/Settings/Communication/components/CalendarIntegrationPage/CompanyAccountSelect';
import VariableList from 'modules/Variable/components/VariableList';

const PaneContainer = Styled.View`
  flex: 1;
  padding-horizontal: 24px;
  overflow: scroll;
`;

const MobileHeaderContainer = Styled.View`
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const HeaderContainer = Styled.View`
  padding-horizontal: 24px;
  padding-vertical: 16px;
  z-index: 1;
`;

const HeadingTextContainer = Styled.View<{mobile: boolean}>`
  flex: 1;
  ${({mobile}) => mobile && 'text-align: center;'}
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const Column = Styled.View`
  flex: 1;
`;

const ContentContainer = Styled.View<{desktop: boolean}>`
  flex: 1;
  background-color: ${colors.gray.background};
  ${({desktop}) => desktop && 'flex-direction: row;'}
  ${({desktop}) => !desktop && 'display: block;'}
  ${({desktop}) => !desktop && 'overflow: scroll;'}
`;

const FooterContainer = Styled.View`
  padding: 16px;
  z-index: 1;
`;

const AccountCalendarFieldsEditable = ({
  organization,
  form,
  field,
}: {
  organization: OrganizationModel;
  form: Form<AccountCalendarFormFields>;
  field: string;
}) => {
  const accountGrantIdField = `${field}.accountGrantId`;
  const accountGrantId = _.get(form.values, accountGrantIdField);

  return (
    <React.Fragment>
      <CompanyAccountSelect
        index={0}
        organization={organization}
        form={form}
        name={accountGrantIdField}
        isRequired
      />
      <Space height={16} />
      <CompanyAccountCalendarSelect
        index={1}
        accountGrantId={accountGrantId}
        form={form}
        name={`${field}.nylasCalendarId`}
        isRequired
      />
    </React.Fragment>
  );
};

const AccountCalendarFieldsReadOnly = ({
  accountCalendar,
}: {
  accountCalendar: AccountCalendarModel;
}) => {
  return (
    <React.Fragment>
      <Text.Label style={{color: colors.gray.secondary}}>{'Company Account'}</Text.Label>
      <Text.Body>{accountCalendar.accountGrant.identifier}</Text.Body>
      <Space height={16} />
      <Text.Label style={{color: colors.gray.secondary}}>{'Calendar'}</Text.Label>
      <Text.Body>{accountCalendar.calendarName}</Text.Body>
    </React.Fragment>
  );
};

const BackgroundContainer = Styled.View`
  background-color: ${colors.white};
  border: 1px solid ${colors.gray.border};
  border-radius: 4px;
  padding: 8px;
`;

const CollapsibleSectionHeaderContainer = Styled.Touchable`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 4px;
`;

const CollapsibleSectionHeader = ({title, onPress, isExpanded}: any) => {
  return (
    <CollapsibleSectionHeaderContainer onPress={onPress}>
      <Text.Subheading>{title}</Text.Subheading>
      <Icon
        source={isExpanded ? Icon.ChevronUp : Icon.ChevronDown}
        color={colors.gray.primary}
        size={16}
      />
    </CollapsibleSectionHeaderContainer>
  );
};

const userRoleOptions = [
  {
    label: 'Salesperson',
    value: UserRole.SALESPERSON,
  },
];

const PROJECT_STATUSES: ProjectStatusType[] = [
  ProjectStatus.LEAD,
  ProjectStatus.HOLD,
  ProjectStatus.BOOKED,
];

const CalendarIntegrationSettingsPane = ({
  organization,
  form,
  field,
  calendarIntegration,
}: {
  organization: OrganizationModel;
  form: Form;
  field: string;
  calendarIntegration?: CalendarIntegrationModel;
}) => {
  const [isVariableListExpanded, setIsVariableListExpanded] = useState(false);

  return (
    <PaneContainer style={{maxWidth: 500}}>
      <Space height={16} />
      {calendarIntegration ? (
        <AccountCalendarFieldsReadOnly accountCalendar={calendarIntegration.accountCalendar} />
      ) : (
        <AccountCalendarFieldsEditable
          organization={organization}
          form={form}
          field={`${field}.accountCalendarForm`}
        />
      )}
      <Space height={16} />
      <FieldInput
        {...form}
        index={3}
        label={'Project Statuses'}
        name={`${field}.projectStatuses`}
        component={MultiDropdownCheckboxInput}
        isResponsive
        isRequired
        input={{
          options: ProjectStatus.OPTIONS.filter((option) =>
            PROJECT_STATUSES.includes(option.value),
          ),
          setFieldValue: form.setFieldValue,
          placeholder: `Select the project statuses`,
          isPortaled: true,
          usePills: true,
          style: {flex: 1},
        }}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        index={2}
        label={'Job Categories'}
        name={`${field}.jobCategories`}
        component={MultiDropdownCheckboxInput}
        isResponsive
        isRequired
        input={{
          options: CalendarIntegrationJobCategoryOptions,
          setFieldValue: form.setFieldValue,
          placeholder: `Select the job categories`,
          isPortaled: true,
          usePills: true,
          style: {flex: 1},
        }}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        index={4}
        label={'Invite Staff'}
        name={`${field}.inviteRoles`}
        component={MultiDropdownCheckboxInput}
        tooltip={
          'When selected, the assigned salesperson will also receive a calendar invite upon export.'
        }
        isResponsive
        input={{
          options: userRoleOptions,
          setFieldValue: form.setFieldValue,
          placeholder: `Select role of the staff`,
          isPortaled: true,
          usePills: true,
          style: {flex: 1},
        }}
      />
      <Space height={16} />
      <BackgroundContainer>
        <CollapsibleSectionHeader
          title={'Variables'}
          onPress={() => setIsVariableListExpanded(!isVariableListExpanded)}
          isExpanded={isVariableListExpanded}
        />
        {isVariableListExpanded && (
          <VariableList
            organization={organization}
            hardcodedSystemVariables={HardcodedSystemVariables.EMAIL_VARIABLES}
            containerStyle={{border: 'none', backgroundColor: colors.white}}
            isCollapsible={false}
          />
        )}
      </BackgroundContainer>
      <Space height={16} />
    </PaneContainer>
  );
};

const CalendarIntegrationEventTemplatePane = ({form, field}: {form: Form; field: string}) => {
  return (
    <PaneContainer>
      <FieldInput
        {...form}
        index={5}
        label={'Event Name'}
        name={`${field}.eventNameTemplate`}
        isResponsive
        isRequired
        input={{
          placeholder: `Enter event name`,
        }}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        index={6}
        label={'Event Description'}
        name={`${field}.eventDescriptionTemplate`}
        component={RichTextInput}
        isResponsive
        showErrorMessage={false}
        style={{flexGrow: 1}}
        input={{
          placeholder: 'Enter event description',
          hasAdvancedFormatting: true,
          setFieldValue: form.setFieldValue,
          autoFocus: false,
          style: {flex: 1},
        }}
      />
      <Space height={24} />
    </PaneContainer>
  );
};

const CalendarIntegrationHeader = ({
  calendarIntegration,
  handleCancel,
  handleSubmit,
  submitting,
}: {
  calendarIntegration?: CalendarIntegrationModel;
  handleCancel: () => void;
  handleSubmit: () => void;
  submitting: boolean;
}) => {
  const responsive = useResponsive();
  const {navigator} = useNavigationDOM();
  const calendarName = calendarIntegration?.accountCalendar.calendarName;

  return (
    <HeaderContainer>
      <Row style={{justifyContent: 'space-between'}}>
        <Column>
          {responsive.desktop ? (
            <Breadcrumbs
              breadcrumbs={[
                {
                  name: 'Calendar Integrations',
                  onPress: () => navigator.replace('/settings/communication/calendar-integrations'),
                },
                {
                  name: calendarIntegration
                    ? `Edit ${_.capitalize(calendarName)}`
                    : 'Create New Integration',
                },
              ]}
            />
          ) : (
            <MobileHeaderContainer style={{flex: 1}}>
              <Space width={16} />
              <Text.Heading2>Create New Integration</Text.Heading2>
              <IconButton source={Icon.Xmark} onPress={handleCancel} isSecondary size={24} />
            </MobileHeaderContainer>
          )}
          <Space height={8} />
          <HeadingTextContainer mobile={responsive.mobile} style={{flex: 1}}>
            <Text.Body style={{color: colors.gray.secondary}}>
              Changes to the calendar integration will only apply to future events.
            </Text.Body>
          </HeadingTextContainer>
        </Column>
        {responsive.desktop && (
          <React.Fragment>
            <Space width={24} />
            <SecondaryButton text={'Cancel'} isResponsive onPress={handleCancel} />
            <Space width={16} />
            <Button
              text={'Save'}
              isResponsive
              iconLeft={Icon.Check}
              onPress={handleSubmit}
              isDisabled={submitting}
            />
          </React.Fragment>
        )}
      </Row>
    </HeaderContainer>
  );
};

const CalendarIntegrationPage = ({
  organization,
  calendarIntegration,
  form,
  field,
  submitting,
  handleSubmit,
  handleCancel,
}: {
  organization: OrganizationModel;
  calendarIntegration?: CalendarIntegrationModel;
  form: Form<UserFlowFormToFormType>;
  field: string;
  submitting: boolean;
  handleSubmit: () => void;
  handleCancel: () => void;
}) => {
  const responsive = useResponsive();
  return (
    <React.Fragment>
      <CalendarIntegrationHeader
        calendarIntegration={calendarIntegration}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        submitting={submitting}
      />
      <Line />
      <ContentContainer desktop={responsive.desktop}>
        <CalendarIntegrationSettingsPane
          organization={organization}
          form={form}
          field={field}
          calendarIntegration={calendarIntegration}
        />
        <CalendarIntegrationEventTemplatePane form={form} field={field} />
      </ContentContainer>
      <Line />
      {responsive.mobile && (
        <FooterContainer>
          <Button
            text={'Save'}
            isResponsive
            iconLeft={Icon.Check}
            onPress={handleSubmit}
            isDisabled={submitting}
            isWidthOfContainer
          />
        </FooterContainer>
      )}
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CalendarIntegrationPage.fragment = gql`
  ${CompanyAccountSelect.fragment}
  ${VariableList.fragment}

  fragment CalendarIntegrationPage on Organization {
    id
    ...CompanyAccountSelect
    ...VariableList
  }
`;

CalendarIntegrationPage.fragment_edit = gql`
  fragment CalendarIntegrationPage_edit on Organization {
    id
    calendarIntegration(uuid: $uuid) {
      id
      accountCalendar {
        id
        calendarName
        accountGrant {
          id
          identifier
        }
      }
    }
  }
`;

export default CalendarIntegrationPage;
